/**
 * EVIDENCE — earned drops and the pending claims that wait on them.
 *
 * A drop is earned by play (ADR-0024) and the server holds it as a pending
 * claim, wallet-free. The view owns no data of its own: it reads the drops
 * (V2-L10b, `GET /core/drops`) and the pending claims (V2-L10c,
 * `GET /core/claims?status=pending`) through `coreSession`, renders them, and
 * forwards player intent (`POST /core/claims/:id/claim`). Claiming is an
 * off-chain stub: the server records the entitlement and never mints; minting
 * is L9. If the line is down or the v2 flag is off, the view says so and
 * leaves the player with an empty, honest screen.
 */
import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage } from '../../stores/coreSession';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';
import { hasLinkedWallet, linkWallet } from '../auth/identityLink';

const state = {
  loaded: false,
  loading: false,
  error: null,
  notice: null,
  busy: null,
  receipt: null,
};

let loadPromise = null;

export function _resetEvidenceState(overrides = {}) {
  Object.assign(
    state,
    { loaded: false, loading: false, error: null, notice: null, busy: null, receipt: null },
    overrides,
  );
  loadPromise = null;
}

export function evidenceErrorMessage(error) {
  if (!error) return 'EVIDENCE LINE DOWN';
  if (error.code === 'CORE-404-012') return 'EVIDENCE LINE CLOSED // V2 OFFLINE';
  return coreErrorMessage(error);
}

export async function loadEvidenceData() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    state.loading = true;
    state.error = null;
    try {
      const session = useCoreSession.getState();
      // Drops only supply provenance for the claims; a drops failure must not
      // hide an empty claims list.
      await Promise.all([
        session.loadClaims('pending'),
        session.loadGrantedClaims().catch(() => []),
        session.loadDrops().catch(() => []),
        session.loadIdentities().catch(() => []),
      ]);
      state.loaded = true;
    } catch (error) {
      state.error = evidenceErrorMessage(error);
      state.loaded = true;
    } finally {
      state.loading = false;
      loadPromise = null;
    }
  })();
  return loadPromise;
}

export function mapPendingClaims(claims = []) {
  return (Array.isArray(claims) ? claims : []).filter(
    (claim) => String(claim?.status || 'pending').toLowerCase() === 'pending',
  );
}

export function mapGrantedClaims(claims = []) {
  return (Array.isArray(claims) ? claims : []).filter(
    (claim) => String(claim?.status || '').toLowerCase() === 'granted',
  );
}

export function findClaimDrop(claim, drops = []) {
  if (!claim?.dropId) return null;
  return (
    (Array.isArray(drops) ? drops : []).find((row) => row?.dropId === claim.dropId) || null
  );
}

export function claimLabel(claim, drops = []) {
  const drop = findClaimDrop(claim, drops);
  const trace = drop?.traceKind || drop?.trigger || drop?.payload?.dropId;
  return String(trace || claim?.dropId || claim?.claimId || 'UNLABELLED').toUpperCase();
}

export function claimRarity(claim, drops = []) {
  const drop = findClaimDrop(claim, drops);
  return String(drop?.rarity || '--').toUpperCase();
}

export function claimProvenance(claim, drops = []) {
  const drop = findClaimDrop(claim, drops);
  if (!drop) return null;
  const trigger = String(drop.trigger || '').toLowerCase();
  const trace = String(drop.traceKind || '').toLowerCase();
  const payload = drop.payload && typeof drop.payload === 'object' ? drop.payload : {};
  if (trigger === 'purge' || trace === 'purge') return 'SURVIVED THE PURGE';
  if (trigger === 'encounter') {
    return payload.encounterId ? `ENCOUNTER ${payload.encounterId}` : 'ENCOUNTER WON';
  }
  if (trigger === 'milestone') {
    return payload.milestoneId ? `MILESTONE ${payload.milestoneId}` : 'SEASON MILESTONE';
  }
  if (trace === 'dead_drop') return 'DEAD DROP RECOVERED';
  if (trigger === 'event') {
    return payload.dropId ? `EVENT ${payload.dropId}` : 'STORY EVENT';
  }
  return trace ? `TRACE ${trace.toUpperCase()}` : null;
}

export function claimEffect(claim, drops = []) {
  const drop = findClaimDrop(claim, drops);
  const payload = drop?.payload && typeof drop.payload === 'object' ? drop.payload : null;
  const effect = typeof payload?.effect === 'string' ? payload.effect.trim() : '';
  if (effect) return effect;
  const provenance = claimProvenance(claim, drops);
  return provenance
    ? `KEPT AS EVIDENCE OF ${provenance}.`
    : 'NO RECORDED EFFECT. KEPT AS EVIDENCE.';
}

export function receiptLines(claim, drops = []) {
  return [
    `WHAT: ${claimLabel(claim, drops)} // ${claimRarity(claim, drops)}`,
    'WHERE: EVIDENCE SHELF // OFF-CHAIN ENTITLEMENT',
    `DOES: ${claimEffect(claim, drops)}`,
  ];
}

function wrapLine(value, maxChars) {
  const limit = Math.max(8, maxChars);
  const words = String(value).split(' ').filter(Boolean);
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > limit && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines.length ? lines : [''];
}

function text(ctx, value, x, y, color = TERMINAL.TEXT, scale = 1) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, scale);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

async function linkAtValue() {
  if (state.busy || state.loading) return;
  state.busy = 'identity-link';
  state.error = null;
  state.notice = 'OPENING IDENTITY LINK...';
  try {
    const { getProvider } = await import('../auth/getProvider');
    const provider = await getProvider();
    const accounts = await provider.request({ method: 'eth_requestAccounts' });
    if (!accounts?.length) throw new Error('No wallet account');
    await linkWallet({
      provider: 'base',
      address: accounts[0],
      signer: (message, address) =>
        provider.request({ method: 'personal_sign', params: [message, address] }),
    });
    await useCoreSession.getState().loadIdentities();
    state.notice = 'IDENTITY LINKED';
  } catch (error) {
    state.error = 'IDENTITY LINK FAILED';
    state.notice = null;
  } finally {
    state.busy = null;
  }
}

async function claimPending(claim) {
  if (!claim?.claimId || state.busy || state.loading) return;
  state.busy = claim.claimId;
  state.error = null;
  state.notice = 'GRANTING CLAIM...';
  try {
    const result = await useCoreSession.getState().claimPendingClaim(claim.claimId);
    const granted = result?.claim || { ...claim, status: 'granted' };
    state.receipt = {
      claim: granted,
      drop: findClaimDrop(granted, useCoreSession.getState().drops),
    };
    state.notice = null;
  } catch (error) {
    state.error = evidenceErrorMessage(error);
    state.notice = null;
  } finally {
    state.busy = null;
  }
}

function drawTop(ctx, safe, session) {
  const mobile = safe.width < 500;
  const online = session.status === 'online';
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, 24);
  rule(ctx, 0, 23, safe.width);
  text(ctx, 'DOPEOS // EVIDENCE', 8, 4, TERMINAL.TEXT);
  if (!mobile) text(ctx, 'EARNED DROPS // PENDING CLAIMS', 216, 4, TERMINAL.TEXT_DIM);
  text(ctx, online ? 'DROPS' : 'OFFLINE', safe.width - 72, 4, online ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawClaims(ctx, x, y, w, h, claims, drops, mobile = false, showLink = false) {
  DopeUI.Panel(ctx, x, y, w, h, 'PENDING CLAIMS');
  text(ctx, 'TRACE / RARITY', x + 10, y + 24, TERMINAL.TEXT_DIM);
  text(ctx, `${String(claims.length).padStart(2, '0')} WAITING`, x + w - 88, y + 24, TERMINAL.TEXT_DIM);
  if (showLink) {
    const bw = 74;
    const bh = 16;
    const bx = x + w - 170;
    const by = y + 18;
    DopeUI.Button(ctx, bx, by, bw, bh, 'LINK ID', 'hover');
    interactiveRegistry.register({
      id: 'evidence-link',
      x: bx,
      y: by,
      w: bw,
      h: bh,
      onClick: () => linkAtValue(),
    });
  }
  rule(ctx, x + 8, y + 40, w - 16);

  const rowH = mobile ? 46 : 30;
  const top = y + 52;
  const maxRows = Math.max(1, Math.floor((h - 58) / rowH));
  const visible = claims.slice(0, maxRows);
  const hidden = claims.length - visible.length;

  visible.forEach((claim, index) => {
    const rowY = top + index * rowH;
    const label = claimLabel(claim, drops);
    const rarity = claimRarity(claim, drops);
    const busy = state.busy === claim.claimId;

    const bw = mobile ? 72 : 64;
    const bh = mobile ? 30 : 20;
    const bx = x + w - bw - 10;
    const by = rowY - (mobile ? 10 : 4);

    if (mobile) {
      text(ctx, `${index + 1} ${label.slice(0, 20)}`, x + 12, rowY, TERMINAL.TEXT);
      text(ctx, `${rarity} // PENDING`, x + 12, rowY + 16, TERMINAL.TEXT_DIM);
    } else {
      const labelMax = Math.max(6, Math.floor((bx - (x + 12)) / 8) - 12);
      const line = `${index + 1} ${label.slice(0, labelMax).padEnd(labelMax)} ${rarity.slice(0, 7)}`;
      text(ctx, line, x + 12, rowY, TERMINAL.TEXT);
    }

    DopeUI.Button(ctx, bx, by, bw, bh, busy ? 'BUSY' : 'CLAIM', busy ? 'disabled' : 'hover');
    if (!busy) {
      interactiveRegistry.register({
        id: `evidence-claim-${claim.claimId}`,
        x: bx,
        y: by,
        w: bw,
        h: bh,
        onClick: () => claimPending(claim),
      });
    }
  });

  if (!claims.length) {
    const message = state.loading || !state.loaded ? 'READING THE LINE...' : 'NO PENDING CLAIMS.';
    text(ctx, message, x + 12, top, TERMINAL.TEXT_DIM);
    if (!state.loading && state.loaded) {
      text(ctx, 'EARNED EVIDENCE WAITS HERE.', x + 12, top + 18, TERMINAL.TEXT_DIM);
      text(ctx, 'NOTHING IS LOST WHILE IT WAITS.', x + 12, top + 36, TERMINAL.TEXT_DIM);
    }
  } else if (hidden > 0) {
    text(ctx, `+${hidden} MORE ON FILE`, x + 12, y + h - 18, TERMINAL.TEXT_DIM);
  }
}

function drawReceipts(ctx, x, y, w, h, granted, drops, mobile = false) {
  DopeUI.Panel(ctx, x, y, w, h, 'GRANTED RECEIPTS');
  const maxChars = Math.max(10, Math.floor((w - 20) / 8));
  const lineH = 15;
  const rowH = mobile ? 32 : 28;
  const bottom = y + h - 10;
  const receipt = state.receipt;
  let cursorY = y + 24;
  const grantedRows = mapGrantedClaims(granted);
  const latestId = receipt?.claim?.claimId || null;

  if (receipt?.claim) {
    text(ctx, 'CLAIM GRANTED', x + 10, cursorY, TERMINAL.TEXT);
    cursorY += lineH + 2;
    receiptLines(receipt.claim, drops).forEach((line) => {
      wrapLine(line, maxChars).forEach((wrapped) => {
        if (cursorY <= bottom - 12) {
          text(ctx, wrapped, x + 10, cursorY, TERMINAL.TEXT_DIM);
          cursorY += lineH;
        }
      });
    });
    cursorY += 4;
  }

  const history = grantedRows.filter((row) => row.claimId !== latestId);
  if (history.length) {
    if (cursorY <= bottom - rowH) {
      text(ctx, receipt?.claim ? 'EARLIER RECEIPTS' : 'GRANTED HISTORY', x + 10, cursorY, TERMINAL.TEXT_DIM);
      cursorY += lineH + 2;
    }
    const maxRows = Math.max(0, Math.floor((bottom - cursorY) / rowH));
    const visible = history.slice(0, maxRows);
    const labelMax = Math.max(6, maxChars - 12);
    visible.forEach((row, index) => {
      const rowY = cursorY + index * rowH;
      const label = claimLabel(row, drops);
      const rarity = claimRarity(row, drops);
      text(ctx, `${index + 1} ${label.slice(0, labelMax)}`, x + 10, rowY, TERMINAL.TEXT);
      text(ctx, `${rarity} // OFF-CHAIN`, x + 10, rowY + 14, TERMINAL.TEXT_DIM);
    });
    const hidden = history.length - visible.length;
    if (hidden > 0) {
      text(ctx, `+${hidden} MORE ON FILE`, x + 10, bottom - 4, TERMINAL.TEXT_DIM);
    }
  } else if (!receipt?.claim) {
    const message = state.loading || !state.loaded ? 'READING THE LINE...' : 'NO RECEIPTS YET.';
    text(ctx, message, x + 10, cursorY, TERMINAL.TEXT_DIM);
    if (!state.loading && state.loaded) {
      text(ctx, 'CLAIMED EVIDENCE LANDS HERE.', x + 10, cursorY + lineH + 2, TERMINAL.TEXT_DIM);
    }
  }
}

function drawFooter(ctx, safe, session) {
  const mobile = safe.width < 500;
  const online = session.status === 'online';
  const canRefresh = online && !state.loading;
  const note = state.error || state.notice;

  if (mobile) {
    const buttonY = 530;
    const backY = 584;
    if (note) text(ctx, `// ${note}`.slice(0, 38), 8, buttonY - 16, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.GREEN);
    DopeUI.Button(ctx, 8, buttonY, safe.width - 16, 44, state.loading ? 'READING...' : 'REFRESH', canRefresh ? 'hover' : 'disabled');
    if (canRefresh) {
      interactiveRegistry.register({
        id: 'evidence-refresh',
        x: 8,
        y: buttonY,
        w: safe.width - 16,
        h: 44,
        onClick: () => loadEvidenceData(),
      });
    }
    DopeUI.Button(ctx, 8, backY, safe.width - 16, 44, 'BACK TO DESKTOP', 'normal');
    interactiveRegistry.register({
      id: 'evidence-back',
      x: 8,
      y: backY,
      w: safe.width - 16,
      h: 44,
      onClick: () => navigateTo('main_menu'),
    });
    return;
  }

  rule(ctx, 0, 320, safe.width);
  text(ctx, 'CLAIM FILED. OFF-CHAIN ENTITLEMENT.', 8, 330, TERMINAL.TEXT_DIM);
  DopeUI.Button(ctx, 8, 344, 304, 28, state.loading ? 'READING...' : 'REFRESH', canRefresh ? 'hover' : 'disabled');
  if (canRefresh) {
    interactiveRegistry.register({
      id: 'evidence-refresh',
      x: 8,
      y: 344,
      w: 304,
      h: 28,
      onClick: () => loadEvidenceData(),
    });
  }
  DopeUI.Button(ctx, 320, 344, safe.width - 328, 28, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'evidence-back',
    x: 320,
    y: 344,
    w: safe.width - 328,
    h: 28,
    onClick: () => navigateTo('main_menu'),
  });
  if (note) text(ctx, `// ${note}`.slice(0, 56), 8, 384, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.GREEN);
}

registerView('evidence', function renderEvidence(ctx, time, width, height) {
  interactiveRegistry.clear();
  const session = useCoreSession.getState();
  if (session.status === 'online' && session.token && !state.loaded && !loadPromise) {
    loadEvidenceData();
  }

  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);

  drawTop(ctx, safe, session);

  const mobile = safe.width < 500;
  const claims = mapPendingClaims(session.claims);
  const granted = session.grantedClaims || [];
  const drops = session.drops || [];
  const showLink =
    Array.isArray(session.identities) && !hasLinkedWallet(session.identities);

  if (session.status !== 'online') {
    DopeUI.Panel(ctx, 8, 36, safe.width - 16, 120, 'NO LINE');
    text(ctx, 'LINE DOWN. LOCAL SHELL.', 20, 70, TERMINAL.TEXT_DIM);
    text(ctx, 'THE PENDING CLAIMS WAIT.', 20, 92, TERMINAL.TEXT_DIM);
  } else if (mobile) {
    drawClaims(ctx, 8, 32, safe.width - 16, 240, claims, drops, true, showLink);
    drawReceipts(ctx, 8, 280, safe.width - 16, 224, granted, drops, true);
  } else {
    drawClaims(ctx, 8, 40, 300, 264, claims, drops, false, showLink);
    drawReceipts(ctx, 316, 40, 276, 264, granted, drops);
  }

  drawFooter(ctx, safe, session);
});

export default 'evidence';
